/**
 * Phase 4 billing, OTP authorization and payment domain types.
 */

export const BILLING_SCHEDULE_STATUSES = [
  'SCHEDULED',
  'DUE',
  'AWAITING_CUSTOMER_AUTHORIZATION',
  'OTP_SENT',
  'PROCESSING',
  'PAID',
  'FAILED',
  'OVERDUE',
  'CANCELLED',
  'SUPERSEDED',
] as const;
export type BillingScheduleStatus = (typeof BILLING_SCHEDULE_STATUSES)[number];

export const PAYMENT_ATTEMPT_STATUSES = [
  'CREATED',
  'AWAITING_AUTHORIZATION',
  'OTP_REQUESTED',
  'OTP_SENT',
  'OTP_EXPIRED',
  'PROCESSING',
  'SUCCEEDED',
  'FAILED',
  'ABANDONED',
  'CANCELLED',
] as const;
export type PaymentAttemptStatus = (typeof PAYMENT_ATTEMPT_STATUSES)[number];

export const PAYMENT_ORDER_STATUSES = [
  'CREATED',
  'PENDING',
  'PROCESSING',
  'SUCCESSFUL',
  'FAILED',
  'EXPIRED',
  'CANCELLED',
  'UNKNOWN',
] as const;
export type PaymentOrderStatus = (typeof PAYMENT_ORDER_STATUSES)[number];

export const AUTHORIZATION_SESSION_STATUSES = [
  'CREATED',
  'LINK_OPENED',
  'OTP_REQUESTED',
  'OTP_SENT',
  'OTP_EXPIRED',
  'SUBMITTED',
  'COMPLETED',
  'INVALIDATED',
  'ABANDONED',
] as const;
export type AuthorizationSessionStatus = (typeof AUTHORIZATION_SESSION_STATUSES)[number];

export const PAYMENT_STATUS_SOURCES = [
  'customer',
  'staff',
  'billing_job',
  'otp_request',
  'otp_submission',
  'status_poll',
  'mock_provider',
  'future_webhook',
] as const;
export type PaymentStatusSource = (typeof PAYMENT_STATUS_SOURCES)[number];

export const MOCK_PAYMENT_SCENARIOS = [
  'success',
  'delayed_success',
  'processing',
  'invalid_otp',
  'expired_otp',
  'insufficient_funds',
  'inactive_mandate',
  'cancelled_mandate',
  'expired_mandate',
  'provider_unavailable',
  'payment_failed',
] as const;
export type MockPaymentScenario = (typeof MOCK_PAYMENT_SCENARIOS)[number];

export const NOTIFICATION_OUTBOX_STATUSES = [
  'PENDING',
  'PROCESSING',
  'SENT',
  'FAILED',
  'CANCELLED',
] as const;
export type NotificationOutboxStatus = (typeof NOTIFICATION_OUTBOX_STATUSES)[number];

export const NOTIFICATION_CHANNELS = ['EMAIL'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export interface RepaymentScheduleBillingView {
  id: string;
  subscriptionId: string;
  sequenceNumber: number;
  dueDate: string;
  instalmentAmount: string;
  amountPaid: string;
  status: BillingScheduleStatus;
  paidAt: string | null;
  overdueAt: string | null;
  activePaymentAttemptId: string | null;
  /** Latest authorization session for the active payment attempt, when one exists. */
  activeAuthorizationSessionId: string | null;
  contractReference: string | null;
  productName: string | null;
}

export interface PaymentAuthorizationView {
  id: string;
  paymentAttemptId: string;
  scheduleId: string;
  subscriptionId: string;
  status: AuthorizationSessionStatus;
  instalmentAmount: string;
  dueDate: string;
  contractReference: string | null;
  productName: string | null;
  mandateActive: boolean;
  bankName: string | null;
  accountMasked: string | null;
  paymentStatus: PaymentAttemptStatus;
  otpRequestedAt: string | null;
  otpExpiresAt: string | null;
  otpResendCount: number;
  otpAttemptCount: number;
  lastResendAt: string | null;
  linkExpiresAt: string;
  maskedEmailDestination: string | null;
}

export interface PaymentAttemptView {
  id: string;
  scheduleId: string;
  subscriptionId: string;
  customerId: string;
  mandateId: string;
  attemptNumber: number;
  internalReference: string;
  amount: string;
  currency: string;
  status: PaymentAttemptStatus;
  providerStatus: PaymentOrderStatus | null;
  failureCode: string | null;
  failureMessage: string | null;
  createdAt: string;
  succeededAt: string | null;
  failedAt: string | null;
}

export interface PaymentTransactionView {
  id: string;
  paymentAttemptId: string;
  scheduleId: string;
  subscriptionId: string;
  internalReference: string;
  providerTransactionId: string | null;
  amount: string;
  currency: string;
  paymentMethod: string;
  status: string;
  providerConfirmedAt: string | null;
  postedAt: string;
}

export interface PaymentReceiptView {
  receiptNumber: string;
  paymentReference: string;
  contractReference: string | null;
  customerName: string;
  identitySource: 'ACCEPTANCE_SNAPSHOT' | 'LEGACY_CURRENT_PROFILE';
  productName: string | null;
  instalmentSequence: number;
  amount: string;
  currency: string;
  paymentMethod: string;
  providerConfirmedAt: string | null;
  postedAt: string;
  bankName: string | null;
  accountMasked: string | null;
  status: string;
  supportEmail: string;
  companyName: string;
}

export interface RequestOtpResultView {
  maskedEmailDestination: string | null;
  otpExpiresAt: string;
  resendCount: number;
  maxResends: number;
  resendCooldownSeconds: number;
}
